import { reportClientError } from './telemetry'

/**
 * Makes the page survive Chrome's built-in translation.
 *
 * The translator rewrites text in place: a text node React is holding onto is
 * replaced by a <font> wrapper of its own making. React, still remembering the
 * old node, later calls `insertBefore`/`removeChild` with a reference that is
 * no longer a child of the parent it expects, the DOM throws NotFoundError
 * ("Failed to execute 'insertBefore' on 'Node': The node before which the new
 * node is to be inserted is not a child of this node"), and the whole screen
 * drops into the error boundary. Our own code never touches the DOM by hand —
 * one `createPortal` aside — so React is the only caller that can hit this.
 *
 * The accounting side reads the app translated, so forbidding translation would
 * cost more than it saves. Make the two calls survivable instead: when the node
 * has been adopted by someone else, skip the operation rather than throw. The
 * text inside a mangled subtree can end up one update behind — the next render
 * that replaces the subtree fixes it — but the session no longer dies.
 *
 * Nothing of this class ever reached `client_errors`, so the guard reports the
 * first skip of each page load: if the translation damage ever grows past
 * cosmetic, we will see it instead of hearing about it.
 */
let installed = false
let reported = false

function reportOnce(op: 'insertBefore' | 'removeChild'): void {
  if (reported) return
  reported = true
  reportClientError({
    kind: 'window',
    message: `dom-guard: ${op} skipped — node belongs to another parent (page translation?)`,
    stack: null,
  })
}

export function installDomMutationGuard(): void {
  if (installed) return
  if (typeof Node !== 'function' || Node.prototype == null) return
  installed = true

  const nativeInsertBefore = Node.prototype.insertBefore
  Node.prototype.insertBefore = function insertBefore<T extends Node>(
    this: Node,
    node: T,
    child: Node | null,
  ): T {
    if (child != null && child.parentNode !== this) {
      reportOnce('insertBefore')
      return node
    }
    return nativeInsertBefore.call(this, node, child) as T
  }

  const nativeRemoveChild = Node.prototype.removeChild
  Node.prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      reportOnce('removeChild')
      return child
    }
    return nativeRemoveChild.call(this, child) as T
  }
}

/** Test-only: lets a test observe the "report once per page" behaviour again. */
export function resetDomGuardReportForTests(): void {
  reported = false
}
